import React from 'react';
import { canAccessQCModule } from '../../utils/roleConstants';
import { Form, FormGroup, FormControl, Button, Row, Col, Table } from 'react-bootstrap';
import config from '../../../config';
import { Link, Navigate, useParams, useNavigate } from 'react-router-dom';
import QValuePageHeader from '../../_components/QValuePageHeader';
import MasterPartNoInput from '../../_components/MasterPartNoInput';
import UploadFiles from '../../_components/upload-files.component';
import UploadService from '../../_services/upload-files.service';
import { getQValueReportTrackerFormHeader } from '../../utils/qValuePageHeaders';
import { getQValueAccountId } from './qValueAccount';
import {
    computeCarDisplayStatus,
    formatDateOnly,
    imageUrlFromPath,
    mapUploadedImageFiles,
    parseJsonArray,
    parseReportMediaArray,
} from './carReportUtils';
import EightDReportPrintView from './EightDReportPrintView';
import { printEightDReport } from './eightDReportPrint';
import {
    DEFAULT_D4_WHYS,
    EIGHT_D_ORIGIN_OPTIONS,
    EIGHT_D_ROW_STATUS_OPTIONS,
    emptyD1TeamRow,
    emptyD3ContainmentRow,
    emptyD6PcaRow,
    latestDateFromRows,
    normalizeD1Rows,
    normalizeD3Rows,
    normalizeD6Rows,
    parseD4Whys,
} from './eightDReportUtils';
import { isReportImageFile, ReportPhotoPreviewList } from './qValueReportMedia';
import QValueReportApprovalBar from './QValueReportApprovalBar';
import {
    canSaveQValueReportDraft,
    isQValueReportReadOnly,
    Q_VALUE_REPORT_RECORD_TYPES,
    shouldShowSubmitForApproval,
} from './qValueReportFormLock';
import { apiFetch } from '../../_services/apiFetch';

const sectionTitleStyle = {
    color: '#1e3a5f',
    fontWeight: 700,
    fontSize: '15px',
    marginTop: '28px',
    marginBottom: '12px',
    textTransform: 'uppercase',
};

const tableHeadStyle = { backgroundColor: '#1e3a5f', color: '#fff' };

const emptyState = () => ({
    id: null,
    reportNo: '',
    title: '',
    status: 'Open',
    originSource: '',
    dateOpened: '',
    revisionLevel: '',
    customerName: '',
    partNo: '',
    partDescription: '',
    ownerName: '',
    leadEngineer: '',
    machineLine: '',
    d1TeamMembers: [emptyD1TeamRow()],
    d2_problem: '',
    d2ProblemPhotos: [],
    d3ContainmentRows: [emptyD3ContainmentRow()],
    d4Whys: { ...DEFAULT_D4_WHYS },
    d5_corrective_action: '',
    d6PcaRows: [emptyD6PcaRow()],
    d7_prevent_recurrence: '',
    d7Attachments: [],
    d8_team_recognition: '',
    saving: false,
    attachMessage: '',
    d2PhotoMessage: '',
    d6PhotoMessage: '',
    isClosedLocked: false,
    approveStatus: 0,
    approvalLabel: '',
    rejectionReason: '',
});

class EightDReportForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = emptyState();
        this.printRef = React.createRef();
    }

    componentDidMount() {
        this.syncRoute();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.params && this.props.params && prevProps.params.id !== this.props.params.id) {
            this.syncRoute();
        }
    }

    getRouteId() {
        const fromParams = this.props.params && this.props.params.id;
        if (fromParams) return fromParams;
        const hash = window.location.hash || '';
        const match = hash.match(/\/eight_d_report\/([^/?]+)/);
        return match ? match[1] : null;
    }

    syncRoute() {
        const id = this.getRouteId();
        if (id && id !== 'new') {
            this.loadRecord(id);
        } else {
            const today = new Date().toISOString().slice(0, 10);
            this.setState(Object.assign(emptyState(), { dateOpened: today }), () => {
                this.loadNextReportNo();
            });
        }
    }

    loadNextReportNo() {
        const company = getQValueAccountId();
        fetch(`${config.apiUrl}/getNextEightDReportNo?company=${company}`)
            .then((r) => r.json())
            .then((data) => {
                if (data && data.reportNo && !this.state.id) {
                    this.setState({ reportNo: data.reportNo });
                }
            })
            .catch(() => {});
    }

    loadRecord(id) {
        const company = getQValueAccountId();
        fetch(`${config.apiUrl}/getEightDReportById?id=${id}&company=${company}`)
            .then((r) => r.json())
            .then((data) => {
                if (!data || !data.id) return;
                this.setState({
                    id: data.id,
                    reportNo: data.reportNo || '',
                    title: data.title || '',
                    status: data.status || 'Open',
                    originSource: data.originSource || '',
                    dateOpened: formatDateOnly(data.dateOpened),
                    revisionLevel: data.revisionLevel || '',
                    customerName: data.customerName || '',
                    partNo: data.partNo || '',
                    partDescription: data.partDescription || '',
                    ownerName: data.ownerName || '',
                    leadEngineer: data.leadEngineer || '',
                    machineLine: data.machineLine || '',
                    d1TeamMembers: normalizeD1Rows(data.d1TeamMembers || data.d1_team),
                    d2_problem: data.d2_problem || data.problemDescription || '',
                    d2ProblemPhotos: parseReportMediaArray(data.d2ProblemPhotos),
                    d3ContainmentRows: normalizeD3Rows(data.d3ContainmentRows || data.d3_containment),
                    d4Whys: parseD4Whys(data.d4Whys, data.d4_root_cause),
                    d5_corrective_action: data.d5_corrective_action || '',
                    d6PcaRows: normalizeD6Rows(data.d6PcaRows || data.d6_implement_verify),
                    d7_prevent_recurrence: data.d7_prevent_recurrence || '',
                    d7Attachments: parseReportMediaArray(data.d7Attachments),
                    d8_team_recognition: data.d8_team_recognition || '',
                    approveStatus: Number(data.approveStatus || data.approve_status || 0),
                    approvalLabel: data.approvalLabel || '',
                    rejectionReason: data.rejectionReason || data.rejection_reason || '',
                    isClosedLocked: !!data.isReadOnly,
                });
            });
    }

    buildSavePayload() {
        const companyId = getQValueAccountId();
        const d6TargetDate = latestDateFromRows(this.state.d6PcaRows, 'targetDate');
        const d6ValidationDate = latestDateFromRows(this.state.d6PcaRows, 'validationDate');
        return {
            id: this.state.id,
            companyId,
            reportNo: this.state.reportNo,
            title: (this.state.d2_problem || '').slice(0, 255),
            status: this.state.status,
            originSource: this.state.originSource,
            dateOpened: this.state.dateOpened || null,
            revisionLevel: this.state.revisionLevel,
            customerName: this.state.customerName,
            partNo: this.state.partNo,
            partDescription: this.state.partDescription,
            ownerName: this.state.ownerName,
            leadEngineer: this.state.leadEngineer,
            machineLine: this.state.machineLine,
            d1TeamMembers: this.state.d1TeamMembers,
            d2_problem: this.state.d2_problem,
            d2ProblemPhotos: this.state.d2ProblemPhotos,
            d3ContainmentRows: this.state.d3ContainmentRows,
            d4Whys: this.state.d4Whys,
            d5_corrective_action: this.state.d5_corrective_action,
            d6PcaRows: this.state.d6PcaRows,
            d6TargetDate: d6TargetDate || null,
            d6ValidationDate: d6ValidationDate || null,
            d7_prevent_recurrence: this.state.d7_prevent_recurrence,
            d7Attachments: this.state.d7Attachments,
            d8_team_recognition: this.state.d8_team_recognition,
        };
    }

    handleStatusChange = (status) => {
        const today = new Date().toISOString().slice(0, 10);
        if (status === 'Closed') {
            const d6PcaRows = this.state.d6PcaRows.map((row, i, arr) => {
                if (i === arr.length - 1 && !row.validationDate) {
                    return { ...row, validationDate: today };
                }
                return row;
            });
            this.setState({ status, d6PcaRows });
            return;
        }
        this.setState({ status, dateClosed: '' });
    };

    updateD1 = (index, field, value) => {
        const d1TeamMembers = this.state.d1TeamMembers.map((row, i) => (
            i === index ? { ...row, [field]: value } : row
        ));
        this.setState({ d1TeamMembers });
    };

    updateD3 = (index, field, value) => {
        const d3ContainmentRows = this.state.d3ContainmentRows.map((row, i) => (
            i === index ? { ...row, [field]: value } : row
        ));
        this.setState({ d3ContainmentRows });
    };

    updateD6 = (index, field, value) => {
        const d6PcaRows = this.state.d6PcaRows.map((row, i) => (
            i === index ? { ...row, [field]: value } : row
        ));
        this.setState({ d6PcaRows });
    };

    updateD4 = (field, value) => {
        this.setState({ d4Whys: { ...this.state.d4Whys, [field]: value } });
    };

    uploadD2Photos = (event) => {
        const files = event.target.files;
        if (!files || !files.length) return;
        UploadService.uploadImage(files)
            .then((response) => {
                const uploaded = mapUploadedImageFiles(response.data);
                this.setState({
                    d2ProblemPhotos: [...this.state.d2ProblemPhotos, ...uploaded],
                    d2PhotoMessage: 'Photo uploaded.',
                });
            })
            .catch(() => this.setState({ d2PhotoMessage: 'Could not upload photo.' }));
    };

    removeD2Photo = (index) => {
        this.setState({
            d2ProblemPhotos: this.state.d2ProblemPhotos.filter((_, i) => i !== index),
        });
    };

    uploadD6ValidationPhoto = (rowIndex, event) => {
        const files = event.target.files;
        if (!files || !files.length) return;
        UploadService.uploadImage(files)
            .then((response) => {
                const uploaded = mapUploadedImageFiles(response.data);
                const d6PcaRows = this.state.d6PcaRows.map((row, i) => {
                    if (i !== rowIndex) return row;
                    const validationPhotos = [...parseReportMediaArray(row.validationPhotos), ...uploaded];
                    return { ...row, validationPhotos };
                });
                this.setState({ d6PcaRows, d6PhotoMessage: 'Validation photo uploaded.' });
            })
            .catch(() => this.setState({ d6PhotoMessage: 'Could not upload validation photo.' }));
        event.target.value = '';
    };

    removeD6ValidationPhoto = (rowIndex, photoIndex) => {
        const d6PcaRows = this.state.d6PcaRows.map((row, i) => {
            if (i !== rowIndex) return row;
            const validationPhotos = parseReportMediaArray(row.validationPhotos).filter((_, j) => j !== photoIndex);
            return { ...row, validationPhotos };
        });
        this.setState({ d6PcaRows });
    };

    uploadD7Files = (event) => {
        const files = Array.from(event.target.files || []);
        if (!files.length) return;

        const imageFiles = files.filter((f) => f.type.startsWith('image/'));
        const otherFiles = files.filter((f) => !f.type.startsWith('image/'));

        const promises = [];
        if (imageFiles.length) {
            promises.push(
                UploadService.uploadImage(imageFiles).then((response) => mapUploadedImageFiles(response.data))
            );
        }
        otherFiles.forEach((file) => {
            promises.push(
                UploadService.upload(file).then((response) => {
                    const name = file.name;
                    const data = response && response.data;
                    const filename = (data && data.filename) || name;
                    return [{
                        originalname: name,
                        filename,
                        imgPath: (data && data.path) || `img/${filename}`,
                        fileType: 'file',
                    }];
                }).catch(() => [{
                    originalname: name,
                    filename: name,
                    imgPath: '',
                    fileType: 'file',
                }])
            );
        });

        Promise.all(promises)
            .then((groups) => {
                const uploaded = groups.flat();
                this.setState({
                    d7Attachments: [...this.state.d7Attachments, ...uploaded],
                    attachMessage: 'Attachment(s) uploaded.',
                });
            })
            .catch(() => this.setState({ attachMessage: 'Upload failed.' }));
    };

    removeAttachment = (index) => {
        this.setState({
            d7Attachments: this.state.d7Attachments.filter((_, i) => i !== index),
        });
    };

    getPrintData() {
        const d6PcaRows = this.state.d6PcaRows;
        const targetDate = latestDateFromRows(d6PcaRows, 'targetDate');
        const dateClosed = latestDateFromRows(d6PcaRows, 'validationDate');
        const status = this.state.status;
        return {
            ...this.state,
            d6TargetDate: targetDate,
            d6ValidationDate: dateClosed,
            targetDate,
            dateClosed,
            problemDescription: this.state.d2_problem,
            displayStatus: computeCarDisplayStatus(status, dateClosed, targetDate),
        };
    }

    handleSave = () => {
        if (!canSaveQValueReportDraft(this.state)) {
            window.alert('This 8D report is closed and read-only.');
            return;
        }
        this.setState({ saving: true });
        const payload = this.buildSavePayload();
        fetch(`${config.apiUrl}/saveEightDReport`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        })
            .then((r) => r.json())
            .then((row) => {
                this.setState({ saving: false });
                if (!row || row.error || !row.id) {
                    window.alert((row && row.error) || 'Save failed.');
                    return;
                }
                window.alert('Saved.');
                if (this.props.navigate) {
                    this.props.navigate('/eight_d_report');
                }
            })
            .catch(() => this.setState({ saving: false }));
    };

    handleSubmitForApproval = () => {
        if (!shouldShowSubmitForApproval(this.state)) return;
        this.setState({ saving: true });
        apiFetch(`${config.apiUrl}/submitEightDReportForApproval`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(this.buildSavePayload()),
        })
            .then((r) => r.json())
            .then((body) => {
                this.setState({ saving: false });
                if (!body || body.error) {
                    window.alert(body && body.error ? body.error : 'Submit for approval failed.');
                    return;
                }
                window.alert(body.message || 'Submitted for approval.');
                if (body.data && body.data.id) {
                    this.loadRecord(body.data.id);
                }
            })
            .catch(() => {
                this.setState({ saving: false });
                window.alert('Submit for approval failed.');
            });
    };

    renderSectionTitle(text) {
        return <div style={sectionTitleStyle}>{text}</div>;
    }

    render() {
        const user = JSON.parse(localStorage.getItem('user'));
        if (!canAccessQCModule(user)) {
            return <Navigate to="/" />;
        }
        const id = this.getRouteId();
        const isNew = !id || id === 'new';
        const company = getQValueAccountId();
        const readOnly = isQValueReportReadOnly(this.state);
        const showSubmitApproval = shouldShowSubmitForApproval(this.state);
        const showSave = canSaveQValueReportDraft(this.state);
        const {
            reportNo, status, originSource, dateOpened, revisionLevel, customerName, partNo,
            partDescription, ownerName, leadEngineer, machineLine, d1TeamMembers, d2_problem,
            d3ContainmentRows, d4Whys, d5_corrective_action, d6PcaRows, d7_prevent_recurrence,
            d7Attachments, d8_team_recognition, saving, attachMessage, approvalLabel, rejectionReason,
        } = this.state;

        return (
            <div className="container">
                <QValuePageHeader
                    {...getQValueReportTrackerFormHeader('eight_d_report', { isNew })}
                />
                {readOnly && (
                    <QValueReportApprovalBar
                        recordType={Q_VALUE_REPORT_RECORD_TYPES.EIGHT_D}
                        recordId={this.state.id}
                        approvalLabel={approvalLabel}
                        rejectionReason={rejectionReason}
                        onDecisionComplete={() => this.loadRecord(this.state.id)}
                    />
                )}
                {readOnly && (
                    <p className="text-muted small mb-3">This 8D report is closed and read-only. Use Print to review.</p>
                )}
                <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                    <Link to="/eight_d_report">← Back to 8D summary</Link>
                    <div className="ms-auto">
                        <Button
                            variant="secondary"
                            type="button"
                            onClick={() => printEightDReport(this.printRef.current, reportNo || '8D-Report')}
                        >
                            Print
                        </Button>
                    </div>
                </div>

                <Form>
                    <fieldset disabled={readOnly} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
                    {this.renderSectionTitle('Report header')}
                    <FormGroup as={Row} className="mb-3 align-items-center flex-md-nowrap">
                        <Col md={3} xs={4} className="col-form-label">
                            <Form.Label className="mb-0">Report ID</Form.Label>
                        </Col>
                        <Col md={9} xs={8}>
                            <FormControl
                                className="qvalue-document-no-field"
                                value={reportNo}
                                readOnly
                                placeholder={isNew && !reportNo ? 'Loading next 8D number…' : 'e.g. 8D-2026-001'}
                            />
                        </Col>
                    </FormGroup>
                    <FormGroup as={Row} className="mb-3 align-items-center">
                        <Col md={3}><Form.Label className="mb-md-0">Date Opened</Form.Label></Col>
                        <Col md={9}><FormControl type="date" value={dateOpened} onChange={(e) => this.setState({ dateOpened: e.target.value })} /></Col>
                    </FormGroup>
                    <FormGroup as={Row} className="mb-3 align-items-center">
                        <Col md={3}><Form.Label className="mb-md-0">Origin / Source</Form.Label></Col>
                        <Col md={9}>
                            <FormControl as="select" value={originSource} onChange={(e) => this.setState({ originSource: e.target.value })}>
                                <option value="">— Select —</option>
                                {EIGHT_D_ORIGIN_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                            </FormControl>
                        </Col>
                    </FormGroup>
                    <FormGroup as={Row} className="mb-3 align-items-center">
                        <Col md={3}><Form.Label className="mb-md-0">Revision</Form.Label></Col>
                        <Col md={9}><FormControl value={revisionLevel} onChange={(e) => this.setState({ revisionLevel: e.target.value })} placeholder="Rev level" /></Col>
                    </FormGroup>
                    <FormGroup as={Row} className="mb-3 align-items-center">
                        <Col md={3}><Form.Label className="mb-md-0">Customer Name</Form.Label></Col>
                        <Col md={9}><FormControl value={customerName} onChange={(e) => this.setState({ customerName: e.target.value })} /></Col>
                    </FormGroup>
                    <FormGroup as={Row} className="mb-3 align-items-center">
                        <Col md={3}><Form.Label className="mb-md-0">Part Number</Form.Label></Col>
                        <Col md={9}>
                            <MasterPartNoInput
                                value={partNo}
                                onChange={(v) => this.setState({ partNo: v })}
                                company={company}
                                onPartDataMatch={(part) => {
                                    if (part && part.description) {
                                        this.setState({ partDescription: part.description });
                                    }
                                }}
                            />
                        </Col>
                    </FormGroup>
                    <FormGroup as={Row} className="mb-3 align-items-center">
                        <Col md={3}><Form.Label className="mb-md-0">Part Description</Form.Label></Col>
                        <Col md={9}><FormControl value={partDescription} readOnly placeholder="Auto from part master" /></Col>
                    </FormGroup>
                    <FormGroup as={Row} className="mb-3 align-items-center">
                        <Col md={3}><Form.Label className="mb-md-0">Owner</Form.Label></Col>
                        <Col md={3}><FormControl value={ownerName} onChange={(e) => this.setState({ ownerName: e.target.value })} /></Col>
                        <Col md={3}><Form.Label className="mb-md-0">Lead Engineer</Form.Label></Col>
                        <Col md={3}><FormControl value={leadEngineer} onChange={(e) => this.setState({ leadEngineer: e.target.value })} /></Col>
                    </FormGroup>
                    <FormGroup as={Row} className="mb-3 align-items-center">
                        <Col md={3}><Form.Label className="mb-md-0">Machine / Line</Form.Label></Col>
                        <Col md={9}><FormControl value={machineLine} onChange={(e) => this.setState({ machineLine: e.target.value })} /></Col>
                    </FormGroup>

                    {this.renderSectionTitle('D1 — Establish the Team')}
                    <div className="table-responsive mb-2">
                        <Table bordered size="sm">
                            <thead><tr style={tableHeadStyle}><th>Role</th><th>Name</th><th>Department</th><th>Contact / Email</th><th /></tr></thead>
                            <tbody>
                                {d1TeamMembers.map((row, index) => (
                                    <tr key={`d1-${index}`}>
                                        <td><FormControl size="sm" value={row.role} onChange={(e) => this.updateD1(index, 'role', e.target.value)} /></td>
                                        <td><FormControl size="sm" value={row.name} onChange={(e) => this.updateD1(index, 'name', e.target.value)} /></td>
                                        <td><FormControl size="sm" value={row.department} onChange={(e) => this.updateD1(index, 'department', e.target.value)} /></td>
                                        <td><FormControl size="sm" value={row.contactEmail} onChange={(e) => this.updateD1(index, 'contactEmail', e.target.value)} /></td>
                                        <td><Button variant="outline-danger" size="sm" onClick={() => this.setState({ d1TeamMembers: d1TeamMembers.filter((_, i) => i !== index).length ? d1TeamMembers.filter((_, i) => i !== index) : [emptyD1TeamRow()] })}>×</Button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                    <Button variant="outline-primary" size="sm" className="mb-3" onClick={() => this.setState({ d1TeamMembers: [...d1TeamMembers, emptyD1TeamRow()] })}>Add team member</Button>

                    {this.renderSectionTitle('D2 — Describe the Problem (5W2H)')}
                    <FormGroup className="mb-3">
                        <FormControl as="textarea" rows={4} value={d2_problem} onChange={(e) => this.setState({ d2_problem: e.target.value })} />
                    </FormGroup>
                    <FormGroup className="mb-3">
                        <Form.Label>Problem photos</Form.Label>
                        <UploadFiles onUpload={this.uploadD2Photos} />
                        {this.state.d2PhotoMessage && (
                            <div className="small text-muted mt-1">{this.state.d2PhotoMessage}</div>
                        )}
                        <ReportPhotoPreviewList items={this.state.d2ProblemPhotos} onRemove={this.removeD2Photo} />
                    </FormGroup>

                    {this.renderSectionTitle('D3 — Interim Containment Actions')}
                    <div className="table-responsive mb-2">
                        <Table bordered size="sm">
                            <thead>
                                <tr style={tableHeadStyle}>
                                    <th>Action Description</th><th>Owner</th><th>Target Date</th>
                                    <th>Impl. Date</th><th>Effectiveness %</th><th>Status</th><th />
                                </tr>
                            </thead>
                            <tbody>
                                {d3ContainmentRows.map((row, index) => (
                                    <tr key={`d3-${index}`}>
                                        <td><FormControl size="sm" value={row.actionDescription} onChange={(e) => this.updateD3(index, 'actionDescription', e.target.value)} /></td>
                                        <td><FormControl size="sm" value={row.owner} onChange={(e) => this.updateD3(index, 'owner', e.target.value)} /></td>
                                        <td><FormControl size="sm" type="date" value={row.targetDate} onChange={(e) => this.updateD3(index, 'targetDate', e.target.value)} /></td>
                                        <td><FormControl size="sm" type="date" value={row.implDate} onChange={(e) => this.updateD3(index, 'implDate', e.target.value)} /></td>
                                        <td><FormControl size="sm" value={row.effectivenessPct} onChange={(e) => this.updateD3(index, 'effectivenessPct', e.target.value)} /></td>
                                        <td>
                                            <FormControl as="select" size="sm" value={row.status} onChange={(e) => this.updateD3(index, 'status', e.target.value)}>
                                                {EIGHT_D_ROW_STATUS_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                                            </FormControl>
                                        </td>
                                        <td><Button variant="outline-danger" size="sm" onClick={() => this.setState({ d3ContainmentRows: d3ContainmentRows.filter((_, i) => i !== index).length ? d3ContainmentRows.filter((_, i) => i !== index) : [emptyD3ContainmentRow()] })}>×</Button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                    <Button variant="outline-primary" size="sm" className="mb-3" onClick={() => this.setState({ d3ContainmentRows: [...d3ContainmentRows, emptyD3ContainmentRow()] })}>Add row</Button>

                    {this.renderSectionTitle('D4 — Root Cause Analysis (5 Whys)')}
                    {[
                        ['why1', 'Why did the issue occur?'],
                        ['why2', 'Why did that happen?'],
                        ['why3', 'Why...?'],
                        ['why4', 'Why...?'],
                        ['why5', 'Why...? (root cause)'],
                    ].map(([key, label]) => (
                        <FormGroup key={key} className="mb-3">
                            <Form.Label>{label}</Form.Label>
                            <FormControl value={d4Whys[key] || ''} onChange={(e) => this.updateD4(key, e.target.value)} />
                        </FormGroup>
                    ))}

                    {this.renderSectionTitle('D5 — Choose & Verify Permanent Corrective Actions (PCA)')}
                    <FormGroup className="mb-3">
                        <FormControl as="textarea" rows={3} value={d5_corrective_action} onChange={(e) => this.setState({ d5_corrective_action: e.target.value })} />
                    </FormGroup>

                    {this.renderSectionTitle('D6 — Implement Permanent Corrective Actions')}
                    <div className="table-responsive mb-2">
                        <Table bordered size="sm">
                            <thead>
                                <tr style={tableHeadStyle}>
                                    <th>PCA Action Item</th><th>Owner</th><th>Target Date</th>
                                    <th>Validation Date</th><th>Validation Method &amp; Result</th><th />
                                </tr>
                            </thead>
                            <tbody>
                                {d6PcaRows.map((row, index) => (
                                    <tr key={`d6-${index}`}>
                                        <td><FormControl size="sm" value={row.pcaActionItem} onChange={(e) => this.updateD6(index, 'pcaActionItem', e.target.value)} /></td>
                                        <td><FormControl size="sm" value={row.owner} onChange={(e) => this.updateD6(index, 'owner', e.target.value)} /></td>
                                        <td><FormControl size="sm" type="date" value={row.targetDate} onChange={(e) => this.updateD6(index, 'targetDate', e.target.value)} /></td>
                                        <td><FormControl size="sm" type="date" value={row.validationDate} onChange={(e) => this.updateD6(index, 'validationDate', e.target.value)} /></td>
                                        <td style={{ minWidth: '200px' }}>
                                            <FormControl
                                                as="textarea"
                                                rows={2}
                                                size="sm"
                                                className="mb-1"
                                                value={row.validationMethodResult}
                                                onChange={(e) => this.updateD6(index, 'validationMethodResult', e.target.value)}
                                            />
                                            <FormControl
                                                type="file"
                                                accept="image/*"
                                                size="sm"
                                                className="mb-1"
                                                onChange={(e) => this.uploadD6ValidationPhoto(index, e)}
                                            />
                                            <ReportPhotoPreviewList
                                                items={parseJsonArray(row.validationPhotos)}
                                                onRemove={(photoIdx) => this.removeD6ValidationPhoto(index, photoIdx)}
                                            />
                                        </td>
                                        <td><Button variant="outline-danger" size="sm" onClick={() => this.setState({ d6PcaRows: d6PcaRows.filter((_, i) => i !== index).length ? d6PcaRows.filter((_, i) => i !== index) : [emptyD6PcaRow()] })}>×</Button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                    <Button variant="outline-primary" size="sm" className="mb-1" onClick={() => this.setState({ d6PcaRows: [...d6PcaRows, emptyD6PcaRow()] })}>Add row</Button>
                    {this.state.d6PhotoMessage && (
                        <div className="small text-muted mb-2">{this.state.d6PhotoMessage}</div>
                    )}
                    <Form.Text className="text-muted d-block mb-3">Summary tracker uses the latest D6 target and validation dates. Add validation evidence photos per PCA row.</Form.Text>

                    {this.renderSectionTitle('D7 — Prevent Recurrence (Systemic Changes)')}
                    <FormGroup className="mb-3">
                        <FormControl as="textarea" rows={3} value={d7_prevent_recurrence} onChange={(e) => this.setState({ d7_prevent_recurrence: e.target.value })} />
                    </FormGroup>
                    <FormGroup className="mb-3">
                        <Form.Label>Attachments (Excel, photo, PDF)</Form.Label>
                        <FormControl type="file" multiple accept="image/*,.pdf,.xlsx,.xls" onChange={this.uploadD7Files} />
                        {attachMessage && <div className="small text-muted mt-1">{attachMessage}</div>}
                        <ReportPhotoPreviewList items={d7Attachments} onRemove={this.removeAttachment} />
                        {d7Attachments.filter((f) => !isReportImageFile(f)).length > 0 && (
                            <ul className="list-unstyled mt-2 mb-0">
                                {d7Attachments.map((f, idx) => (
                                    isReportImageFile(f) ? null : (
                                        <li key={`${f.filename}-${idx}`} className="d-flex gap-2 align-items-center mb-1">
                                            {f.imgPath ? (
                                                <a href={imageUrlFromPath(f.imgPath)} target="_blank" rel="noopener noreferrer">{f.originalname || f.filename}</a>
                                            ) : (
                                                <span>{f.originalname || f.filename}</span>
                                            )}
                                            <Button variant="link" className="text-danger p-0" size="sm" onClick={() => this.removeAttachment(idx)}>Remove</Button>
                                        </li>
                                    )
                                ))}
                            </ul>
                        )}
                    </FormGroup>

                    {this.renderSectionTitle('D8 — Recognize & Congratulate the Team')}
                    <FormGroup className="mb-3">
                        <FormControl as="textarea" rows={2} value={d8_team_recognition} onChange={(e) => this.setState({ d8_team_recognition: e.target.value })} />
                    </FormGroup>

                    {this.renderSectionTitle('Submit for Approval')}
                    <FormGroup as={Row} className="mb-4 align-items-center">
                        <Col md={3}><Form.Label className="mb-md-0">Status</Form.Label></Col>
                        <Col md={9}>
                            <FormControl as="select" value={status} disabled={readOnly} onChange={(e) => this.handleStatusChange(e.target.value)}>
                                <option value="Open">Open</option>
                                <option value="In Progress">In Progress</option>
                                <option value="Closed" disabled={isNew}>Closed</option>
                            </FormControl>
                        </Col>
                    </FormGroup>

                    {showSave && (
                        <Button variant="primary" disabled={saving} onClick={this.handleSave}>
                            {isNew ? 'Create 8D' : 'Save 8D'}
                        </Button>
                    )}
                    {showSubmitApproval && (
                        <Button variant="success" disabled={saving} className="ms-2" onClick={this.handleSubmitForApproval}>
                            Submit for Approval
                        </Button>
                    )}
                    </fieldset>
                </Form>

                <div aria-hidden style={{ position: 'fixed', left: '-200vw', top: 0, width: '794px', pointerEvents: 'none' }}>
                    <div ref={this.printRef} style={{ width: '794px', background: '#fff' }}>
                        <EightDReportPrintView data={this.getPrintData()} />
                    </div>
                </div>
            </div>
        );
    }
}

function withRouter(Component) {
    return (props) => {
        const params = useParams();
        const navigate = useNavigate();
        return <Component {...props} params={params} navigate={navigate} />;
    };
}

const EightDReportFormWithRouter = withRouter(EightDReportForm);
export { EightDReportFormWithRouter as EightDReportForm };
